/**
 * The log screen — the design's single page. One baby at a time (tabs), the
 * two numbers that matter at a glance, up to five one-tap actions (sleep and
 * changes only if the family tracks them), and today's timeline. Every action closes its sheet and confirms immediately; the
 * network catches up behind an optimistic cache. On a tablet the actions stay
 * pinned in a left column while the timeline scrolls beside them.
 */
import { amountText, BOTTLE_PRESETS, fromOz, localCalendar, toOz } from "@babia/checks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FeedbackButton } from "../components/feedback.tsx";
import { useAssistantTip } from "../components/log/assistant-tip.tsx";
import { BabyTabs } from "../components/log/baby-tabs.tsx";
import { LogHeader } from "../components/log/header.tsx";
import { HomeScreenCard, useHomeScreenStep } from "../components/log/home-screen-step.tsx";
import { PlanNotice } from "../components/log/plan-notice.tsx";
import { QuickActions } from "../components/log/quick-actions.tsx";
import { AssistantChangeSheet } from "../components/log/sheets/assistant-change.tsx";
import { BottleSheet } from "../components/log/sheets/bottle.tsx";
import { ChangeSheet } from "../components/log/sheets/change.tsx";
import { EditSheet } from "../components/log/sheets/edit.tsx";
import { MedsSheet } from "../components/log/sheets/meds.tsx";
import { NoteSheet } from "../components/log/sheets/note.tsx";
import { Stats } from "../components/log/stats.tsx";
import { Timeline } from "../components/log/timeline.tsx";
import { useToast } from "../components/ui/toast.tsx";
import { UpdateNotice } from "../components/update-notice.tsx";
import { track } from "../lib/analytics.ts";
import {
  editSummary,
  type EntryPatch,
  isEmptyPatch,
  undoPatch,
  withPatch,
} from "../lib/entry-edit.ts";
import {
  type DiaperKind,
  entryEmoji,
  type EntryLike,
  entryName,
  medsSummary,
  namesText,
  noteSummary,
  TYPE_EMOJI,
  WAKE_EMOJI,
  type WeightShown,
} from "../lib/entry-text.ts";
import { percentileWords, reasonWords, todayReason, useGrowth } from "../lib/growth.ts";
import { useNight } from "../lib/night.tsx";
import { canLog } from "../lib/plan.ts";
import {
  bottlesForAll,
  type Entry,
  type EntryCreateBody,
  type Household,
  isPending,
  newTempId,
  optimisticEntry,
  useBabies,
  useBottleUnit,
  useCreateEntry,
  useDeleteEntry,
  useEndSleep,
  useEntries,
  useEntryChanges,
  useEntryPages,
  useHousehold,
  useReportTimeZone,
  useRestoreChange,
  useTracking,
  useUpdateEntry,
  useWeightUnit,
} from "../lib/queries.ts";
import { type EntryChange, marksByEntry, removedRows, whenText } from "../lib/revisions.ts";
import { useSelectedBaby } from "../lib/selected-baby.tsx";
import { deriveStats } from "../lib/stats.ts";
import { clock } from "../lib/time.ts";
import { LIVE_WINDOW_MS, olderEntries } from "../lib/timeline.ts";
import { useNow } from "../lib/use-now.ts";

type SheetKind = "bottle" | "change" | "meds" | "note" | null;

/** The entry open in the edit sheet. */
interface Editing {
  /** The entry as it was when the sheet opened: the edit's starting point. */
  base: Entry;
  /** Where the edit goes: a new entry's temporary id until the server saves it. */
  id: string;
  /** A save of the entry is on its way (a new entry, or a Woke up tap): Save waits for it. */
  waiting: boolean;
  /** A new one for each opening, so the sheet's draft starts fresh. */
  session: number;
}

const NO_ENTRIES: Entry[] = [];
/** The household defaults (services/household.ts), until its own list loads. */
const DEFAULT_MEDS: Household["medPresets"] = ["Vitamin D drops", "Gas drops", "Probiotic"].map(
  (name) => ({ name, dose: null }),
);

export function Log() {
  const babies = useBabies();
  const entries = useEntries();
  const household = useHousehold();
  const tracking = useTracking();
  const unit = useBottleUnit();
  const weightUnit = useWeightUnit();
  useReportTimeZone();
  const now = useNow(20_000);
  const { babyId, setBabyId } = useSelectedBaby();
  const toast = useToast();
  const createEntry = useCreateEntry();
  const endSleep = useEndSleep();
  const deleteEntry = useDeleteEntry();
  const changes = useEntryChanges();
  const restoreChange = useRestoreChange();
  const updateEntry = useUpdateEntry();
  // The assistant change open in its sheet.
  const [changeOpen, setChangeOpen] = useState<EntryChange | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const editSessions = useRef(0);
  // Saves still on their way, by the id their row shows (a new entry's
  // temporary one, or a nap a Woke up tap is ending): an edit waits for them.
  const saving = useRef(new Map<string, Promise<Entry>>());
  // The Home Screen step waits for daylight and a log that takes taps. The
  // assistant tip also waits for entries, and for the step to be answered.
  const { night } = useNight();
  const homeScreen = useHomeScreenStep(
    !night && household.data !== undefined && canLog(household.data.plan),
  );
  const assistantTip = useAssistantTip(
    (entries.data?.length ?? 0) > 0 && !night && !homeScreen.step,
  );
  const removing = useRef(new Set<string>());
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const dismissToast = toast.dismiss;
  useEffect(() => () => dismissToast(), [dismissToast]);
  const [sheet, setSheet] = useState<SheetKind>(null);
  const closeSheet = useCallback(() => setSheet(null), []);
  const openSheet = (kind: Exclude<SheetKind, null>) => {
    setSheet(kind);
    track("log sheet opened", { sheet: kind });
  };

  const list = babies.data ?? [];
  const baby = list.find((b) => b.id === babyId) ?? list[0];
  const marks = useMemo(() => marksByEntry(changes.data ?? []), [changes.data]);
  const data = useMemo(
    () => (entries.data ?? NO_ENTRIES).filter((e) => !hiddenIds.has(e.id)),
    [entries.data, hiddenIds],
  );
  const stats = useMemo(
    () => deriveStats(data, baby?.id ?? "", now, unit),
    [data, baby?.id, now, unit],
  );
  // Older entries load only once asked for, per baby, and stay open on this screen.
  const [historyOpen, setHistoryOpen] = useState<ReadonlySet<string>>(new Set());
  const showsHistory = !!baby && historyOpen.has(baby.id);
  const pages = useEntryPages(baby?.id ?? "", showsHistory);
  const older = useMemo(
    () =>
      showsHistory
        ? olderEntries(pages.data?.pages, entries.data, entries.dataUpdatedAt).filter(
            // A row just moved to the other baby stays in this baby's pages until the refresh.
            (e) => e.babyId === baby?.id && !hiddenIds.has(e.id),
          )
        : NO_ENTRIES,
    [showsHistory, pages.data, entries.data, entries.dataUpdatedAt, hiddenIds, baby?.id],
  );
  // Removed rows show only as far back as the timeline's entries reach: the
  // live window, the loaded pages, or everything once the last page is in. An
  // entry put back there then shows again with the next refresh.
  const allLoaded = showsHistory && pages.isSuccess && !pages.hasNextPage;
  const shownSince = useMemo(
    () =>
      allLoaded
        ? new Date(0)
        : older.reduce(
            (oldest, e) => (e.at < oldest ? e.at : oldest),
            new Date(now.getTime() - LIVE_WINDOW_MS),
          ),
    [allLoaded, older, now],
  );
  const removed = useMemo(
    () => removedRows(changes.data ?? [], baby?.id ?? "", now, shownSince),
    [changes.data, baby?.id, now, shownSince],
  );
  // The WHO table loads once this baby's timeline shows a weight with a number.
  const { growth } = useGrowth(
    data.some((e) => e.babyId === baby?.id && typeof e.weightGrams === "number") ||
      older.some((e) => typeof e.weightGrams === "number"),
  );
  const asleep = useMemo(
    () =>
      new Set(
        tracking.sleep
          ? data.filter((e) => e.type === "sleep" && !e.endedAt).map((e) => e.babyId)
          : [],
      ),
    [data, tracking.sleep],
  );
  // The baby on screen when a toast's action runs, after any tab switch.
  const onScreen = useRef<string | undefined>(undefined);
  useEffect(() => {
    onScreen.current = baby?.id;
  });

  if (!baby) return null; // AppShell redirects to /setup before this renders

  const name = baby.name;
  // After an unpaid trial the log is read-only: no actions, no ×.
  const plan = household.data?.plan;
  const writable = canLog(plan);
  const feedLabel = household.data?.feedLabel ?? "Similac";
  // The household's sizes in its unit; the unit's usual sizes until it loads.
  const presets = household.data
    ? household.data.ozPresets.map((oz) => fromOz(oz, unit))
    : [...BOTTLE_PRESETS[unit]];
  const medPresets = household.data?.medPresets ?? DEFAULT_MEDS;
  /** A weight's number in the household's unit, with its percentile once the table loads. */
  const weightOf = (e: EntryLike): WeightShown => ({
    unit: weightUnit,
    percentile:
      typeof e.weightGrams === "number" ? percentileWords(growth, baby, e.weightGrams, e.at) : null,
  });
  const weightReason = todayReason(baby, localCalendar.isoDate(now));
  const weightHint = weightReason ? reasonWords(weightReason, name, baby.dueDate) : null;

  const babyNameOf = (id: string) => list.find((b) => b.id === id)?.name ?? "Another baby";

  /**
   * Send one new entry; its row shows at once. Returns the save, and `edit`,
   * which opens the entry's edit sheet (on the saved row once it exists).
   */
  function post(entry: EntryCreateBody & { at: Date }) {
    const tempId = newTempId();
    let created: Entry | null = null;
    // A promise per entry: `mutate`'s own callbacks fire only for the latest call.
    const saved = createEntry.mutateAsync({ ...entry, tempId });
    saving.current.set(tempId, saved);
    void saved
      .then(
        (row) => {
          created = row;
        },
        () => {},
      )
      .finally(() => saving.current.delete(tempId));
    return {
      saved,
      edit: () => openEdit(created ?? optimisticEntry(entry, tempId, entry.at), "toast"),
    };
  }

  /**
   * Log an entry at `at` (now, unless the bottle sheet's time moved): close
   * the sheet, confirm now, roll back with a message if it fails. The toast's
   * Edit opens the new entry's edit sheet.
   */
  function log(body: Omit<EntryCreateBody, "babyId">, at = new Date()) {
    toast.dismiss();
    setSheet(null);
    const { saved, edit } = post({ babyId: baby!.id, at, ...body });
    void saved.catch((err: Error) => toast.show(`Couldn't save — ${err.message}`));
    toast.show(
      body.type === "sleep"
        ? `${name} fell asleep at ${clock(at)}`
        : `${body.type === "meds" ? medsSummary(body) : body.type === "note" ? noteSummary(body, weightUnit) : entryName(body)} logged for ${name} at ${whenText(at, new Date())}`,
      { emoji: entryEmoji(body), actionLabel: "Edit", onAction: edit },
    );
  }

  /**
   * Log the same bottle for every baby at once (#89): one post per baby, in
   * tab order, with the sheet's one time and the household's feed label. A
   * post that fails takes only its own row away, and a toast names that baby.
   * The toast's Edit opens the bottle of the baby on screen at the tap.
   */
  function logForAll(amount: number, at: Date) {
    toast.dismiss();
    setSheet(null);
    const bodies = bottlesForAll(
      list.map((b) => b.id),
      at,
      toOz(amount, unit),
      feedLabel,
    );
    const posts = bodies.map((body) => ({
      babyId: body.babyId,
      name: babyNameOf(body.babyId),
      ...post(body),
    }));
    void Promise.allSettled(posts.map((p) => p.saved)).then((results) => {
      const failed = posts.filter((_, i) => results[i]!.status === "rejected");
      const first = results.find((r) => r.status === "rejected");
      if (!first) return;
      toast.show(
        `Couldn't save the ${failed.length > 1 ? "bottles" : "bottle"} for ${namesText(failed.map((p) => p.name))} — ${(first.reason as Error).message}`,
      );
    });
    toast.show(
      `${amountText(amount, unit)} logged for ${namesText(list.map((b) => b.name))} at ${whenText(at, new Date())}`,
      {
        emoji: TYPE_EMOJI.feed,
        actionLabel: "Edit",
        onAction: () => (posts.find((p) => p.babyId === onScreen.current) ?? posts[0])?.edit(),
      },
    );
  }

  function toggleSleep() {
    const open = stats.openSleep;
    if (open) {
      if (isPending(open)) return;
      toast.dismiss();
      const endedAt = new Date();
      // An edit of a late Wake up waits for the end to save, so an end that
      // failed never becomes the value the edit goes back to.
      const ended = endSleep.mutateAsync({ id: open.id, endedAt });
      saving.current.set(open.id, ended);
      void ended
        .catch((err: Error) => toast.show(`Couldn't save — ${err.message}`))
        .finally(() => {
          if (saving.current.get(open.id) === ended) saving.current.delete(open.id);
        });
      toast.show(`${name} woke up at ${clock(endedAt)}`, {
        emoji: WAKE_EMOJI,
        actionLabel: "Edit",
        onAction: () => openEdit({ ...(open as Entry), endedAt }, "toast"),
      });
    } else {
      log({ type: "sleep" });
    }
  }

  /** Open the edit sheet; Save waits for a save of the entry still on its way. */
  function openEdit(entry: Entry, from: "row" | "toast") {
    const saved = saving.current.get(entry.id);
    // A temporary row whose save already failed: nothing left to edit.
    if (isPending(entry) && !saved) return;
    // A toast's Edit can come while another sheet is open: this one replaces it.
    setSheet(null);
    setChangeOpen(null);
    const session = ++editSessions.current;
    setEditing({ base: entry, id: entry.id, waiting: !!saved, session });
    track("entry edit opened", { entry_type: entry.type, from });
    saved?.then(
      (row) =>
        setEditing((e) => (e?.session === session ? { ...e, id: row.id, waiting: false } : e)),
      () => setEditing((e) => (e?.session === session ? null : e)),
    );
  }

  /** Save only what changed, confirm now, and offer Undo for five seconds. */
  function saveEdit(patch: EntryPatch) {
    const open = editing;
    setEditing(null);
    if (!open || isEmptyPatch(patch)) return;
    // Commit a removal still waiting on Undo first; the edit queues behind it.
    toast.dismiss();
    const { base, id } = open;
    const after = withPatch(base, patch);
    void updateEntry
      .mutateAsync({ id, patch })
      .catch((err: Error) => toast.show(`Couldn't save — ${err.message}`));
    // A weight's number or a dose added to an entry can't be taken off again.
    const undo = undoPatch(base, patch);
    toast.show(
      editSummary(base, after, { unit, weightUnit, now: new Date(), babyName: babyNameOf }),
      {
        emoji: entryEmoji(after),
        ...(undo
          ? {
              actionLabel: "Undo",
              onAction: () => {
                track("entry edit undone", { entry_type: base.type });
                void updateEntry
                  .mutateAsync({ id, patch: undo })
                  .catch((err: Error) => toast.show(`Couldn't undo — ${err.message}`));
                toast.show(`${entryName(base)} for ${babyNameOf(base.babyId)} back as it was`, {
                  emoji: entryEmoji(base),
                });
              },
            }
          : {}),
      },
    );
  }

  /** Open this baby's older entries, or load the next page of them. */
  function showEarlier() {
    if (!showsHistory) {
      setHistoryOpen((open) => new Set(open).add(baby!.id));
      track("earlier entries shown", { page: 1 });
    } else if (pages.isError && !pages.isFetchNextPageError) {
      // The first page or a refresh failed, even with every page loaded: try again.
      void pages.refetch();
    } else if (pages.hasNextPage) {
      track("earlier entries shown", { page: (pages.data?.pages.length ?? 0) + 1 });
      void pages.fetchNextPage();
    }
  }

  /** Show what an assistant changed in a row. */
  function openChange(change: EntryChange) {
    setChangeOpen(change);
    track("assistant change opened", { kind: change.kind, entry_type: change.before.type });
  }

  /** Put an assistant's change back: an edit's old values, or the removed entry. */
  function putBack(change: EntryChange) {
    // Commit a removal still waiting on Undo first, as a new entry does: the
    // restore queues behind it (a nap removed to make room for this one).
    toast.dismiss();
    const what = `${entryName(change.before)} at ${clock(change.before.at)}`;
    restoreChange.mutate(change.id, {
      onSuccess: () => {
        setChangeOpen(null);
        toast.show(change.kind === "delete" ? `${what} is back` : `${what} put back as it was`, {
          emoji: entryEmoji(change.before),
        });
      },
      onError: (err) => toast.show(`Couldn't put it back — ${err.message}`),
    });
  }

  /** Hide immediately; Undo cancels deletion without recreating the entry. */
  function remove(e: Entry) {
    if (isPending(e) || removing.current.has(e.id)) return;
    track("entry removal started", { entry_type: e.type });
    removing.current.add(e.id);
    setHiddenIds(new Set(removing.current));
    const reveal = () => {
      removing.current.delete(e.id);
      setHiddenIds(new Set(removing.current));
    };
    toast.show(`${entryName(e)} at ${clock(e.at)} removed`, {
      actionLabel: "Undo",
      onAction: () => {
        track("entry removal undone", { entry_type: e.type });
        reveal();
        toast.show(`${entryName(e)} at ${clock(e.at)} restored`);
      },
      onDismiss: () => {
        // A replaced toast or leaving the log also commits the removal.
        void deleteEntry
          .mutateAsync(e.id)
          .catch((err: Error) => {
            toast.show(`Couldn't remove — ${err.message}`);
          })
          .finally(reveal);
      },
    });
  }

  return (
    <>
      <LogHeader
        now={now}
        tip={{ open: assistantTip.open, babyName: name, unit, onAnswer: assistantTip.retire }}
      />
      <UpdateNotice />
      <div className="md:grid md:grid-cols-2 md:items-start md:gap-10 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:gap-14">
        <div className="md:sticky md:top-[calc(16px+env(safe-area-inset-top))]">
          <BabyTabs
            babies={list}
            selectedId={baby.id}
            asleep={asleep}
            onSelect={(id) => {
              if (id !== baby.id) track("baby tab switched", { babies: list.length });
              setBabyId(id);
            }}
          />
          <Stats stats={stats} diaper={tracking.diaper} />
          <PlanNotice plan={plan} now={now} night={night} />
          {writable ? (
            <QuickActions
              stats={stats}
              busy={!!stats.openSleep && isPending(stats.openSleep)}
              // Until the household loads, its unit is a guess: a 90 ml bottle
              // would be saved as 90 oz, so the button waits (a few hundred ms).
              bottleReady={household.data !== undefined}
              // A nap left open when sleep was turned off keeps its tile until it ends.
              sleep={tracking.sleep || !!stats.openSleep}
              diaper={tracking.diaper}
              onBottle={() => openSheet("bottle")}
              onChange={() => openSheet("change")}
              onSleepToggle={toggleSleep}
              onMeds={() => openSheet("meds")}
              onNote={() => openSheet("note")}
            />
          ) : null}
          {homeScreen.step ? (
            <HomeScreenCard step={homeScreen.step} onDone={homeScreen.retire} />
          ) : null}
          {entries.isError ? (
            <p className="mt-6 text-[14px] text-accent-2">
              Couldn't load the log — {entries.error.message}
            </p>
          ) : null}
        </div>
        <Timeline
          babyName={name}
          unit={unit}
          weightOf={weightOf}
          today={stats.today as Entry[]}
          earlier={stats.earlier as Entry[]}
          older={older}
          more={{
            loading:
              showsHistory &&
              (pages.isPending || pages.isFetchingNextPage || (pages.isError && pages.isFetching)),
            done: showsHistory && pages.isSuccess && !pages.hasNextPage,
            error: showsHistory && pages.isError ? pages.error.message : null,
            onShow: showEarlier,
          }}
          now={now}
          changeOf={(e) => marks.get(e.id)}
          removed={removed}
          putting={restoreChange.isPending ? (restoreChange.variables ?? null) : null}
          onRemove={writable ? remove : undefined}
          onEdit={writable ? (e) => openEdit(e, "row") : undefined}
          onChange={openChange}
          onPutBack={writable ? putBack : undefined}
          className="md:mt-[22px]"
        />
      </div>
      {/* Below everything, out of the 3am path. */}
      <footer className="mt-10 flex justify-center border-t border-divider pt-3">
        <FeedbackButton
          placement="log"
          className="ghost-btn inline-flex min-h-[44px] items-center gap-2 px-3 text-[14px] opacity-70"
        >
          <span aria-hidden="true">💬</span>
          Feedback
        </FeedbackButton>
      </footer>

      <AssistantChangeSheet
        change={changeOpen}
        unit={unit}
        weightUnit={weightUnit}
        babyNameOf={babyNameOf}
        now={now}
        putting={restoreChange.isPending}
        onClose={() => setChangeOpen(null)}
        onPutBack={writable ? putBack : undefined}
      />
      <EditSheet
        // A unit that loads (or changes) while the sheet is open starts the
        // draft again in that unit, so its numbers never read in the wrong one.
        key={editing ? `${editing.session}-${unit}-${weightUnit}` : 0}
        base={editing?.base ?? null}
        saving={!!editing?.waiting}
        babies={list}
        asleep={asleep}
        unit={unit}
        weightUnit={weightUnit}
        presets={presets}
        usual={fromOz(stats.usualOz, unit)}
        medPresets={medPresets}
        now={now}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
      />
      <BottleSheet
        open={sheet === "bottle"}
        babyName={name}
        babyCount={list.length}
        unit={unit}
        usual={fromOz(stats.usualOz, unit)}
        presets={presets}
        now={now}
        onClose={closeSheet}
        onSave={(amount, at) =>
          log({ type: "feed", amountOz: toOz(amount, unit), label: feedLabel }, at)
        }
        onSaveAll={writable ? logForAll : undefined}
      />
      <ChangeSheet
        open={sheet === "change"}
        babyName={name}
        onClose={closeSheet}
        onPick={(kind: DiaperKind) => log({ type: "diaper", diaperKind: kind })}
      />
      <MedsSheet
        open={sheet === "meds"}
        babyName={name}
        presets={medPresets}
        now={now}
        onClose={closeSheet}
        onLog={(label, dose) =>
          log({
            type: "meds",
            label,
            ...(dose ? { doseAmount: dose.amount, doseUnit: dose.unit } : {}),
          })
        }
      />
      <NoteSheet
        open={sheet === "note"}
        babyName={name}
        weightUnit={weightUnit}
        weightHint={weightHint}
        onClose={closeSheet}
        onSave={(kind, text, grams) =>
          log({
            type: "note",
            noteKind: kind,
            ...(text ? { note: text } : {}),
            ...(grams === null ? {} : { weightGrams: grams }),
          })
        }
      />
    </>
  );
}
